import { Dialog } from "@rio-cloud/rio-uikit";
import { useIntl } from "react-intl";
import { OccurrenceStepLog } from "../../../models/occurrence";
import { formatMilisecondsInDaysHoursMinutes } from "../../../utils/dateUtils";

interface StepHistoryModalProps {
  show: boolean;
  onClose: () => void;
  stepLabel?: string;
  logs: OccurrenceStepLog[];
  isLastLogOngoing: boolean;
}

const formatDateTime = (locale: string, date?: Date | null) =>
  date
    ? new Date(date).toLocaleString(locale, {
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "-";

const StepHistoryModal = ({
  show,
  onClose,
  stepLabel,
  logs,
  isLastLogOngoing,
}: StepHistoryModalProps) => {
  const intl = useIntl();
  const getLogDuration = (log: OccurrenceStepLog, isCurrent: boolean) => {
    if (!log?.startDate) return 0;

    const startTime = new Date(log.startDate).getTime();
    const endTime = isCurrent ? new Date().getTime() : new Date(log.endDate).getTime();

    return endTime - startTime;
  };

  const durations = logs.map((log, index) =>
    getLogDuration(log, isLastLogOngoing && index === logs.length - 1)
  );
  const totalTime = durations.reduce((acc, duration) => acc + duration, 0);

  return (
    <Dialog
      show={show}
      title={
        stepLabel
          ? intl.formatMessage({ id: "intl-msg:kanban.stepHistoryModal.titleWithStep" }, { stepLabel })
          : intl.formatMessage({ id: "intl-msg:kanban.stepHistoryModal.title" })
      }
      onClose={onClose}
      bsSize={Dialog.SIZE_SM}
      footer={
        <div className="display-flex justify-content-end">
          <button className="btn btn-sm btn-outline" onClick={onClose}>
            {intl.formatMessage({ id: "intl-msg:kanban.stepHistoryModal.close" })}
          </button>
        </div>
      }
      body={
        <div className="flex flex-col gap-3">
          {logs.map((log, index) => {
            const isCurrent = isLastLogOngoing && index === logs.length - 1;
            return (
              <div key={index}>
                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="font-semibold mb-1">
                    {intl.formatMessage(
                      { id: isCurrent ? "intl-msg:kanban.stepHistoryModal.currentPassage" : "intl-msg:kanban.stepHistoryModal.passage" },
                      { count: index + 1 }
                    )}
                  </div>
                  <div className="flex justify-between text-size-14">
                    <span>{intl.formatMessage({ id: "intl-msg:kanban.stepHistoryModal.start" })}</span>
                    <span>{formatDateTime(intl.locale, log.startDate)}</span>
                  </div>
                  <div className="flex justify-between text-size-14">
                    <span>{intl.formatMessage({ id: "intl-msg:kanban.stepHistoryModal.end" })}</span>
                    <span>{isCurrent ? intl.formatMessage({ id: "intl-msg:kanban.stepHistoryModal.inProgress" }) : formatDateTime(intl.locale, log.endDate)}</span>
                  </div>
                </div>
                <div className="flex justify-between text-size-14 px-1 mt-1">
                  <span>{intl.formatMessage({ id: "intl-msg:kanban.stepHistoryModal.passageTime" })}</span>
                  <span className="font-bold">
                    {formatMilisecondsInDaysHoursMinutes(durations[index])}
                  </span>
                </div>
              </div>
            );
          })}

          {logs.length > 0 && (
            <div className="flex justify-between items-center border-t border-gray-200 pt-3 font-semibold">
              <span>{intl.formatMessage({ id: "intl-msg:kanban.stepHistoryModal.totalStepTime" })}</span>
              <span>{formatMilisecondsInDaysHoursMinutes(totalTime)}</span>
            </div>
          )}
        </div>
      }
    />
  );
};

export default StepHistoryModal;
